import { useMemo, useState } from 'react';
import { appendCapiumTag, capiumTagGroupsFor } from '@/core/models/capium-tags';
import {
  narrativeEditorConfig,
  NARRATIVE_NOTE_DEFAULTS,
  type NarrativeNoteType,
  type NoteDialogData,
  type NoteRow,
} from '@/core/models/notes.models';
import { NoteTagsTab } from '@/features/settings-b/NoteTagsTab';
import type { DialogComponentProps } from '@/kit/dialog';
import { RichTextEditor } from '@/kit/RichTextEditor';
import { NoteDialogShell, useNoteSave } from './NoteDialogParts';
import {
  auditInformationText,
  balanceSheetText,
  financialCommitText,
  indebtednessText,
  limitedByGuaranteeText,
} from './noteTexts';

/**
 * ap5-a5: a check-box note whose wording is printed on the accounts. The saved text wins; a note that has none
 * shows the default wording.
 */
function NarrativeNoteDialog({
  data,
  dialogRef,
  noteType,
  defaultTitle,
  guidanceClassName,
  paragraphs,
}: DialogComponentProps<NoteDialogData, NoteRow> & {
  noteType: NarrativeNoteType;
  defaultTitle: string;
  guidanceClassName: string;
  paragraphs: string[];
}) {
  const [contents, setContents] = useState<string>(
    () => data?.note?.content?.contents || NARRATIVE_NOTE_DEFAULTS[noteType],
  );
  const editorConfig = useMemo(() => narrativeEditorConfig(), []);
  const { isSaving, save } = useNoteSave(data, dialogRef);

  return (
    <NoteDialogShell title={data?.title || defaultTitle} isSaving={isSaving} onSave={() => save(() => ({ contents }))}>
      <div className={guidanceClassName}>
        {paragraphs.map((text, index) => (
          <p key={index}>{text}</p>
        ))}
      </div>

      <div className="d-flex gap-15 pt-5">
        <div className="form-field w-510">
          <p className="fw-bold">Note Contents</p>
          {/* APT247: the formatting toolbar lines up under the box (NotesDialogs.scss .note-editor). */}
          <div className="note-editor">
            <RichTextEditor value={contents} onChange={setContents} config={editorConfig} />
          </div>
        </div>
        <NoteTagsTab
          groups={capiumTagGroupsFor(data?.clientType ?? (data?.isLlp ? 3 : null))}
          onInsert={(tag) => setContents((current) => appendCapiumTag(current, tag))}
        />
      </div>
    </NoteDialogShell>
  );
}

export function BalanceSheetDialog(props: DialogComponentProps<NoteDialogData, NoteRow>) {
  return (
    <NarrativeNoteDialog
      {...props}
      noteType="offBalanceSheet"
      defaultTitle="Off Balance Sheet Arrangements"
      guidanceClassName="w-630"
      paragraphs={balanceSheetText.paragraphs}
    />
  );
}

export function IndebtednessDialog(props: DialogComponentProps<NoteDialogData, NoteRow>) {
  return (
    <NarrativeNoteDialog
      {...props}
      noteType="indebtedness"
      defaultTitle="Indebtedness"
      guidanceClassName="w-510"
      paragraphs={indebtednessText.paragraphs}
    />
  );
}

export function FinancialCommitDialog(props: DialogComponentProps<NoteDialogData, NoteRow>) {
  return (
    <NarrativeNoteDialog
      {...props}
      noteType="financialCommitments"
      defaultTitle="Financial Commitments, Guarantees and Contingencies"
      guidanceClassName="w-630"
      paragraphs={financialCommitText.paragraphs}
    />
  );
}

export function AuditInformationDialog(props: DialogComponentProps<NoteDialogData, NoteRow>) {
  return (
    <NarrativeNoteDialog
      {...props}
      noteType="auditInformation"
      defaultTitle="Audit Information"
      guidanceClassName="w-510"
      paragraphs={auditInformationText.paragraphs}
    />
  );
}

export function LimitedByGuaranteeDialog(props: DialogComponentProps<NoteDialogData, NoteRow>) {
  return (
    <NarrativeNoteDialog
      {...props}
      noteType="limitedByGuarantee"
      defaultTitle="Company Limited by Guarantee"
      guidanceClassName="w-510"
      paragraphs={limitedByGuaranteeText.paragraphs}
    />
  );
}
